<template>
  <div>
    <cl-table-wrapper
      class-name="tw-bg-primary-grey-25 tw-px-0 tw-mt-0 tw-pt-0"
    >
      <template #before>
        <div class="tw-flex tw-items-center tw-justify-between tw-pb-2">
          <div>
            <label for="search-msps" class="tw-sr-only">
              {{ t("search") }}
            </label>
            <div class="tw-relative md:tw-max-w-xs">
              <cl-input-search
                id="search-msps"
                :name="'search-msps'"
                :test-id="`${props.dataTestId}-search`"
                :placeholder="t('search')"
                :size="'medium'"
                :value="filterState"
                :readonly="isRequestPending"
                @update:value="debounceSearch"
              />
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <template v-if="selectedMsps.length">
              <system-delete-button-action
                v-if="areAllSelectedSystems"
                :orgs-to-delete="selectedMsps"
                :data-test-id="props.dataTestId"
              />
              <customer-delete-button-action
                v-else-if="areAllSelectedCustomers"
                :accounts-to-delete="selectedMsps"
              />
              <msp-delete-button-action
                v-else
                :msps-to-delete="selectedMsps"
                :data-test-id="props.dataTestId"
              />
            </template>
            <msp-add-button-action
              :loading="isRequestPending"
              @created="getOrganisationsList()"
            />
            <cl-table-column-visibility-toggle>
              <div
                v-for="column in columnsToggleList"
                :key="column.id"
                class="tw-mb-2"
              >
                <label class="tw-flex tw-items-center tw-gap-2">
                  <cl-input-checkbox
                    id="send_customer_statistics"
                    :checked="column.getIsVisible()"
                    :disabled="
                      column.getIsVisible() &&
                      table.getVisibleLeafColumns().length <= 2
                    "
                    :indeterminate="false"
                    name="send_customer_statistics"
                    @change="toggleColumnVisibility(column)"
                  />
                  {{ column.columnDef.header }}
                </label>
              </div>
            </cl-table-column-visibility-toggle>
          </div>
        </div>
      </template>
      <template #table>
        <cl-table>
          <cl-thead>
            <cl-tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
              :table-target="'thead'"
            >
              <cl-th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :col-span="header.colSpan"
                :class="{
                  'tw-select-none': header.column.getCanSort(),
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    header.column.getIsPinned(),
                }"
                scope="col"
              >
                <div class="tw-flex tw-h-7 tw-items-center tw-justify-between">
                  <button
                    v-if="header.column.getCanSort()"
                    class="tw-flex tw-items-center tw-h-7"
                    :class="{
                      'tw-text-primary-grey-300': isRequestPending,
                    }"
                    :data-test-id="`${props.dataTestId}-header-${header.id}`"
                    :disabled="isRequestPending"
                    @click="header.column.getToggleSortingHandler()?.($event)"
                  >
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                      <cl-th-sorting
                        :sort-direction="header.column.getIsSorted()"
                      />
                    </template>
                  </button>
                  <div class="tw-flex tw-items-center tw-h-7" v-else>
                    <template v-if="!header.isPlaceholder">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                      <span
                        v-if="header.column.columnDef.meta?.headerTooltip"
                        v-tippy="{
                          content: header.column.columnDef.meta?.headerTooltip,
                          placement: 'top',
                        }"
                        class="tw-text-primary-grey-200 tw-ml-2"
                        @click.stop
                      >
                        <CbIconInformation :width="18" :height="18" />
                      </span>
                    </template>
                  </div>
                </div>
              </cl-th>
            </cl-tr>
          </cl-thead>
          <cl-tbody v-if="isRequestPending">
            <cl-tr-skeleton :rows="skeletonArray" />
          </cl-tbody>
          <cl-tbody v-else-if="!mspsList.length">
            <cl-tr zebra>
              <cl-td
                colspan="100%"
                :class-name="'tw-text-center tw-pl-0 tw-pr-0 tw-pb-0'"
              >
                <t-empty>
                  <template #image>
                    <component :is="emptyState.icon" />
                  </template>
                  <template #title>
                    <span :data-test-id="`${props.dataTestId}-empty-state`">
                      {{ emptyState.title }}
                    </span>
                  </template>
                  <template #description>
                    {{ emptyState.description }}
                  </template>
                </t-empty>
              </cl-td>
            </cl-tr>
          </cl-tbody>
          <cl-tbody v-else data-clarity-mask="true">
            <cl-tr
              v-for="(row, index) in table.getRowModel().rows"
              :key="row.id"
              :zebra="index % 2 === 0"
            >
              <cl-td
                v-for="(cell, cellIndex) in row.getVisibleCells()"
                :key="cell.id"
                :class="{
                  'tw-sticky tw-right-0 tw-bg-[inherit] tw-ml-auto':
                    cell.column.getIsPinned(),
                }"
                :data-test-id="`${props.dataTestId}-tr-${index}-td-${cellIndex}`"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </cl-td>
            </cl-tr>
          </cl-tbody>
        </cl-table>
      </template>
      <template #after>
        <div class="tw-relative">
          <div v-if="isRequestPending"><cl-skeleton-row /></div>
          <div v-else class="tw-flex tw-justify-between tw-items-baseline">
            <cl-table-page-size
              :placeholder="t('rows-per-page')"
              :page-size="table.getState().pagination.pageSize"
              :page-sizes="[10, 25, 50]"
              :handle-page-size-change="
                ($e: Event) =>
                  handlePageSizeChange(
                    Number(($e.target as HTMLInputElement).value),
                  )
              "
            />
            <cl-table-pagination-state
              :start="rowsIndexPointer.startIndex"
              :end="rowsIndexPointer.endIndex"
              :total="total.total"
              :placeholder="t('of')"
            />
            <cl-table-basic-pagination
              :previous-page-text="$t('previous-pagination')"
              :next-page-text="$t('next')"
              :can-previous-page="table.getCanPreviousPage()"
              :previous-page="table.previousPage"
              :can-next-page="table.getCanNextPage()"
              :next-page="table.nextPage"
            />
          </div>
        </div>
      </template>
    </cl-table-wrapper>
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import TEmpty from "@/components/TEmpty";
import { computed, h, ref, resolveComponent, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { isDevBuild } from "@/config/runtimeConfig";
import {
  useRowsIndexPointer,
  useSkeletonArray,
  useServerSidePagination,
  useMemoDefaultParams,
  useMemoColumnVisibility,
  useColumnVisibility,
} from "@/composables/table-helpers";
import { useDebounceFn } from "@vueuse/core";
import {
  useVueTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender,
  ColumnDef,
  type RowSelectionState,
} from "@tanstack/vue-table";
import dayjs from "dayjs";
import { Msp } from "./types";
import TEnabledSolutionsQuickView from "@/components/TEnabledSolutionsQuickView";
import { DATA_TABLE_COOKIE } from "@/constants";
import { AvailableServicesInterface } from "@/components/TEnabledSolutionsQuickView/TEnabledSolutionsQuickView.vue";
import MspDeleteButtonAction from "@/views/System/Overview/MspTable/MspDeleteButtonAction/MspDeleteButtonAction.vue";
import SystemDeleteButtonAction from "@/views/System/Overview/MspTable/SystemDeleteButtonAction/SystemDeleteButtonAction.vue";
import CustomerDeleteButtonAction from "@/modules/Customers/CustomersTable/CustomerDeleteButtonAction/CustomerDeleteButtonAction.vue";
import MspAddButtonAction from "@/views/System/Overview/MspTable/MspAddButtonAction/MspAddButtonAction.vue";
import MspDataTableActions from "@/views/System/Overview/MspTable/MspDataTableActions/MspDataTableActions.vue";
import { transformOrganisationsToMsp } from "@/views/System/Overview/MspTable/utils/transformOrganisationsToMsp.ts";
import TUpdateSystems from "@/modals/TUpdateSystems.vue";
import TUpdateMspsIndex from "@/modals/t-update-msps/t-update-msps.index.vue";
import TUpdateCustomer from "@/modals/t-update-customer/t-update-customer.index.vue";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { caseInsensitiveTableSort } from "@/utils/caseInsensitiveTableSort";
import { isAuth0UserMigrationComplete } from "@/utils/auth0Migration";
import UuidTablePopup from "@/components/UuidTablePopup/UuidTablePopup.vue";
import { ACCOUNT_TYPE } from "@/constants";
import { navigateToMspOrganisation } from "@/utils/navigation/organisationNavigation";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

interface Props {
  dataTestId: string;
  multiSelect?: boolean;
}

const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const organisationsStore = useOrganisationsStore();
const { orginisationsList: organisationsList, organisationsTotal: total } =
  storeToRefs(organisationsStore);
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { setRequestStatus, isRequestPending } = useRequestStatus();

const props = withDefaults(defineProps<Props>(), {
  multiSelect: false,
});

const cookieKey = ref<string>(DATA_TABLE_COOKIE.SYSTEM_MSPS_LIST);

const mspsList = computed(() =>
  transformOrganisationsToMsp(
    organisationsList.value as unknown as Parameters<
      typeof transformOrganisationsToMsp
    >[0],
  ),
);

const selectedMsp = computed(() => authStore.selectedMsp);

const getOrganisationsList = async () => {
  const parentUuid =
    selectedMsp.value?.uuid ||
    (route.params?.orgUuid as string | undefined) ||
    null;
  setRequestStatus({ type: PENDING });
  try {
    if (isDevBuild()) {
    }
    await organisationsStore.getOrginisationsList({
      parent_uuid: parentUuid,
      params: {
        page: paramsState.page,
        results: paramsState.results,
        sortDir: paramsState.sortDir.toLocaleUpperCase() || "ASC",
        sortKey: paramsState.sortKey || "name",
        filter: paramsState.filter || "",
      },
    });
  } finally {
    setRequestStatus({ type: READY });
  }
};

const columns: ColumnDef<Msp>[] = [
  {
    id: "select",
    accessorKey: "select",
    enableSorting: false,
    header: ({ table }) =>
      h(resolveComponent("cl-input-checkbox"), {
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        disabled: !props.multiSelect,
      }),
    // v2 org = managed in Auth0; render no checkbox so the row cannot be
    // selected and therefore cannot be bulk-deleted.
    cell: ({ row }) =>
      isAuth0UserMigrationComplete(row.original.auth0_migration_version)
        ? ""
        : h(resolveComponent("cl-input-checkbox"), {
            checked: row.getIsSelected(),
            indeterminate: !row.getCanSelect(),
            disabled:
              !row.getCanSelect() ||
              row.original.organisation_type === "Distributor",
            "data-test-id": `${props.dataTestId}-checkbox-${row.index}`,
            onChange: row.getToggleSelectedHandler(),
          }),
    enableHiding: false,
  },
  {
    header: t("name"),
    accessorKey: "name",
    sortingFn: caseInsensitiveTableSort,
  },
  {
    header: t("uuid-title"),
    accessorKey: "uuid",
    enableSorting: false,
    meta: {
      headerTooltip: t("uuid-tooltip"),
    },
    cell: ({ getValue }) => {
      const uuid = getValue<string>();

      return h(UuidTablePopup, {
        uuid: uuid,
      });
    },
  },
  {
    header: t("description-placeholder"),
    accessorKey: "description",
    enableSorting: false,
  },
  {
    header: t("label-type"),
    accessorKey: "organisation_type",
    enableSorting: false,
  },
  {
    header: t("active-users"),
    accessorKey: "seats_used",
    enableSorting: false,
  },
  {
    header: t("licenses-purchased-legend"),
    accessorKey: "seats_purchased",
    enableSorting: false,
  },
  {
    header: t("date-created"),
    accessorKey: "created_at",
    cell: ({ getValue }) => dayjs(getValue<Date>()).format("MMM D, YYYY"),
  },
  {
    header: t("solutions"),
    accessorKey: "solutions",
    enableSorting: false,
    cell: ({ getValue }) => {
      return h(TEnabledSolutionsQuickView, {
        dataTestId: props.dataTestId,
        services: getValue<AvailableServicesInterface[]>(),
      });
    },
  },
  {
    header: t("actions"),
    accessorKey: "actions",
    enableSorting: false,
    cell: ({ row }) => {
      const rowIndex = Number(row.index);
      const {
        uuid,
        name,
        organisation_type,
        parent_uuid,
        parent_name,
        auth0_migration_version,
      } = mspsList.value[rowIndex];

      let editComponent;
      if (
        organisation_type?.toLowerCase() === ACCOUNT_TYPE.SYSTEM.toLowerCase()
      ) {
        editComponent = TUpdateSystems;
      } else if (
        organisation_type?.toLowerCase() === ACCOUNT_TYPE.CUSTOMER.toLowerCase()
      ) {
        editComponent = TUpdateCustomer;
      } else {
        editComponent = TUpdateMspsIndex;
      }

      return h(MspDataTableActions, {
        dataTestId: props.dataTestId,
        rowIndex: rowIndex,
        mspUuid: uuid,
        organisationType: organisation_type,
        auth0MigrationVersion: auth0_migration_version,
        disabled: organisation_type === "Distributor",
        onUpdated: () => getOrganisationsList(),
        onActionView: () =>
          redirectToOrganisation({
            id: uuid,
            name,
            type: organisation_type,
            parentUuid: parent_uuid,
            parentName: parent_name,
          }),
        viewUrl: viewUrlForOrganisation({
          id: uuid,
          uuid,
          type: organisation_type,
        }),
        editComponent: editComponent,
      });
    },
  },
];

const { defaultParams, handleParamsChange } = useMemoDefaultParams(
  cookieKey.value,
  {
    pageSize: 10,
    sortDir: "asc",
    sortKey: "name",
    filter: "",
  },
);
const selectedRows = ref<RowSelectionState>({});
const selectedMsps = computed(() => {
  const list = [];

  for (const id of Object.keys(selectedRows.value)) {
    const msp = mspsList.value.find((msp) => msp.uuid === id);
    if (msp) {
      list.push(msp);
    }
  }
  return list;
});
const areAllSelectedSystems = computed(
  () =>
    selectedMsps.value.length > 0 &&
    selectedMsps.value.every(
      (msp) =>
        msp.organisation_type?.toLowerCase() ===
        ACCOUNT_TYPE.SYSTEM.toLowerCase(),
    ),
);
const areAllSelectedCustomers = computed(
  () =>
    selectedMsps.value.length > 0 &&
    selectedMsps.value.every(
      (msp) =>
        msp.organisation_type?.toLowerCase() ===
        ACCOUNT_TYPE.CUSTOMER.toLowerCase(),
    ),
);

const { columnVisibility } = useMemoColumnVisibility(
  `${cookieKey.value}-columns`,
  {
    uuid: false,
  },
);

const {
  paramsState,
  filterState,
  sortingState,
  paginationState,
  handlePageSizeChange,
} = useServerSidePagination({
  pageIndex: 0,
  pageSize: defaultParams.value.pageSize,
  sortDir: defaultParams.value.sortDir,
  sortKey: defaultParams.value.sortKey,
  filter: "",
});

const emptyState = computed(() =>
  paramsState.filter
    ? {
        icon: "cl-icon-empty-folder",
        title: t("no-search-results-default"),
        description: t("subtext-no-search-results-default"),
      }
    : {
        icon: "cl-icon-empty-box",
        title: t("no-search-results-default"),
        description: t("no-customers"),
      },
);

const debounceSearch = useDebounceFn((phrase: string) => {
  filterState.value = phrase;
}, 500);

let isResettingForNavigation = false;
const resetTableStateForNavigation = async () => {
  if (isResettingForNavigation) return;

  isResettingForNavigation = true;
  filterState.value = "";
  debounceSearch("");
  paginationState.value.pageIndex = 0;
  paramsState.filter = "";
  paramsState.page = 1;
  handleParamsChange({
    pageSize: paramsState.results,
    sortDir: paramsState.sortDir,
    sortKey: paramsState.sortKey,
    filter: "",
  });
  await nextTick();
  isResettingForNavigation = false;
};

const navigateToSubSystem = async (row: {
  id?: string;
  uuid?: string;
  name: string;
}) => {
  const targetId = row.id || row.uuid;
  if (!targetId) return;
  await resetTableStateForNavigation();
  await authStore.setCustomer(null);
  await authStore.setMspUser({
    uuid: targetId,
    name: row.name,
  });
  if (isDevBuild()) {
  }
  pageMetaStore.setMSP(row.name || "");
  pageMetaStore.setPageTitle({
    title: row.name,
  });
  router.push({
    name: "systemOverview",
    params: { orgUuid: targetId },
  });
};

const navigateToCustomer = async (row: {
  id: string;
  name: string;
  parentUuid?: string;
  parentName?: string;
}) => {
  await authStore.setMspUser(null);
  pageMetaStore.setMSP(row.parentName || "");

  await authStore.setCustomer({
    uuid: row.id,
    name: row.name,
  });

  router.push(buildOrgUrl("customer", row.id));
};

const navigateToMsp = async (row: {
  id?: string;
  uuid?: string;
  name: string;
}) => {
  const targetId = row.id || row.uuid;
  if (!targetId) return;
  await navigateToMspOrganisation({ router }, { id: targetId, name: row.name });
};

const redirectToOrganisation = async (row: {
  id: string;
  name: string;
  type: string;
  parentUuid?: string;
  parentName?: string;
}) => {
  const orgType = row.type?.toLowerCase?.() ?? "";
  const isSystem = orgType === ACCOUNT_TYPE.SYSTEM.toLowerCase();
  const isCustomer = orgType === ACCOUNT_TYPE.CUSTOMER.toLowerCase();

  if (isSystem) {
    await navigateToSubSystem(row);
    return;
  }

  if (isCustomer) {
    await navigateToCustomer(row);
    return;
  }

  await navigateToMsp(row);
};

const viewUrlForOrganisation = (row: {
  id: string;
  uuid?: string;
  type: string;
}) => {
  const targetId = row.id || row.uuid;
  if (!targetId) return "";
  const orgType = row.type?.toLowerCase?.() ?? "";
  if (orgType === ACCOUNT_TYPE.SYSTEM.toLowerCase()) {
    return buildOrgUrl("system", targetId, "dashboard");
  }
  if (orgType === ACCOUNT_TYPE.CUSTOMER.toLowerCase()) {
    return buildOrgUrl("customer", targetId);
  }
  return buildOrgUrl("msp", targetId, "dashboard");
};

const table = useVueTable({
  get data() {
    return mspsList.value;
  },
  get pageCount() {
    return Math.ceil(total.value.total / defaultParams.value.pageSize);
  },
  initialState: {
    columnPinning: {
      right: ["actions"],
    },
  },
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getSortedRowModel: getSortedRowModel(),
  onPaginationChange: (updater) => {
    updater instanceof Function
      ? (paginationState.value = updater(paginationState.value))
      : (paginationState.value = updater);
  },
  onSortingChange: (updater) => {
    updater instanceof Function
      ? (sortingState.value = updater(sortingState.value))
      : (sortingState.value = updater);
  },
  getRowId: (row) => row.uuid,
  onRowSelectionChange: (updater) => {
    updater instanceof Function
      ? (selectedRows.value = updater(selectedRows.value))
      : (selectedRows.value = updater);
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
    get sorting() {
      return sortingState.value;
    },
    get rowSelection() {
      return selectedRows.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
  manualPagination: true,
  manualSorting: true,
  // v2 orgs are excluded from selection entirely, so "select all" skips them too.
  enableRowSelection: (row) =>
    !isAuth0UserMigrationComplete(row.original.auth0_migration_version),
  enableMultiRowSelection: props.multiSelect,
});

const rowsIndexPointer = useRowsIndexPointer(table);

watch(
  [paramsState, selectedMsp],
  async (newValues, oldValues) => {
    const [newParams, newSelectedMsp] = newValues;
    const [, oldSelectedMsp] = oldValues || [null, null];

    // If selectedMsp changed, reset pagination and filter
    if (newSelectedMsp !== null && newSelectedMsp !== oldSelectedMsp) {
      await resetTableStateForNavigation();
      getOrganisationsList();
      return;
    }

    // Skip fetch if we're in the middle of resetting
    if (isResettingForNavigation) {
      return;
    }

    // Otherwise handle normal paramsState changes
    getOrganisationsList();
    handleParamsChange({
      pageSize: newParams.results,
      sortDir: newParams.sortDir,
      sortKey: newParams.sortKey,
      filter: newParams.filter,
    });
  },
  { immediate: true, deep: true },
);

watch(
  () => route.params.orgUuid,
  () => {
    if (isDevBuild()) {
    }
  },
  { immediate: true },
);

const skeletonArray = useSkeletonArray(table, 10);
const { columnsToggleList, toggleColumnVisibility } = useColumnVisibility(
  table,
  columnVisibility,
);
</script>
